import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Gift, Calendar, Star, PartyPopper } from "lucide-react";
import Confetti from "react-confetti";
import { useToast } from "@/hooks/use-toast";

interface GiftReceivedPopupProps {
  isOpen: boolean;
  onDismiss: () => void;
  onAccept: () => Promise<boolean>;
  data: {
    pledgeId: string;
    goalTitle?: string;
    reward?: string;
    dueDate?: string;
    senderName?: string;
    message?: string;
  } | null;
}

export function GiftReceivedPopup({
  isOpen,
  onDismiss,
  onAccept,
  data,
}: GiftReceivedPopupProps) {
  const [accepting, setAccepting] = useState(false);
  const { toast } = useToast();

  if (!data) return null;

  const handleAccept = async () => {
    setAccepting(true);
    const success = await onAccept();
    setAccepting(false);
    if (success) {
      toast({
        title: "🎉 Gift accepted!",
        description: data.goalTitle
          ? `"${data.goalTitle}" has been added to your goals.`
          : "You've accepted this gift promise.",
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to accept gift. Try from the Connections tab.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onDismiss()}>
      <DialogContent className="gd-obsidian sm:max-w-md overflow-hidden">
        {isOpen && (
          <Confetti
            width={400}
            height={500}
            recycle={false}
            numberOfPieces={200}
            gravity={0.3}
            style={{ position: "absolute", top: 0, left: 0, zIndex: 0, pointerEvents: "none" }}
          />
        )}

        <div className="relative z-10 flex flex-col items-center text-center py-4 space-y-4">
          {/* Animated gift icon */}
          <div className="relative">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-[0_16px_36px_-14px_hsl(40_90%_50%/0.8),inset_0_1px_0_hsl(45_100%_92%/0.9)] animate-bounce">
              <span className="text-4xl">🎁</span>
            </div>
            <PartyPopper className="absolute -top-2 -right-2 h-6 w-6 text-amber-300 animate-pulse" />
          </div>

          <div className="space-y-1">
            <p className="gd-kicker text-amber-200/70">A gift for you</p>
            <h2 className="gd-display text-xl sm:text-2xl">
              You received a gift promise!
            </h2>
            <p className="gd-obs-muted text-sm">
              From <span className="font-semibold text-white">{data.senderName || "Someone"}</span>
            </p>
          </div>

          {/* Gift details card */}
          <div className="gd-obs-tile w-full space-y-3 p-4">
            {data.goalTitle && (
              <div className="flex items-start gap-2">
                <Star className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                <div className="text-left">
                  <p className="gd-obs-muted text-xs">Goal</p>
                  <p className="text-sm font-semibold">{data.goalTitle}</p>
                </div>
              </div>
            )}
            {data.reward && (
              <div className="flex items-start gap-2">
                <Gift className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                <div className="text-left">
                  <p className="gd-obs-muted text-xs">Reward</p>
                  <p className="text-sm font-semibold">{data.reward}</p>
                </div>
              </div>
            )}
            {data.dueDate && (
              <div className="flex items-start gap-2">
                <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                <div className="text-left">
                  <p className="gd-obs-muted text-xs">Due Date</p>
                  <p className="text-sm font-semibold">{data.dueDate}</p>
                </div>
              </div>
            )}
            {data.message && (
              <p className="gd-obs-muted border-t border-white/10 pt-2 text-sm italic">
                "{data.message}"
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex w-full gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1 rounded-full border-white/20 bg-white/[0.06] text-white hover:bg-white/[0.12] hover:text-white"
              onClick={onDismiss}
            >
              View Later
            </Button>
            <Button
              className="flex-1 rounded-full border-0 bg-gradient-to-b from-amber-300 to-amber-400 font-bold text-amber-950 shadow-[0_12px_28px_-12px_hsl(40_90%_50%/0.8)] hover:brightness-105"
              onClick={handleAccept}
              disabled={accepting}
            >
              {accepting ? "Accepting..." : "🎉 Accept Gift"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
